import {
  type FormEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/Elements/Button';
import { Callout } from '@/components/Elements/Callout';
import { type AuthUser } from '@/features/auth/types';
import { ProfileSection } from './ProfileSection';
import { UserProfileFields } from './UserProfileForm';
import { useUserProfileForm } from './useUserProfileForm';

type UserProfileSettingsFormProps = {
  user: AuthUser;
  profileNotice?: ReactNode;
  contractSection: ReactNode;
  onSaved?: () => void;
  onUnsavedChangesChange?: (hasUnsavedChanges: boolean) => void;
  onSavingChange?: (isSaving: boolean) => void;
  showSavedMessage?: boolean;
};

export const UserProfileSettingsForm = ({
  user,
  profileNotice,
  contractSection,
  onSaved,
  onUnsavedChangesChange,
  onSavingChange,
  showSavedMessage = true,
}: UserProfileSettingsFormProps) => {
  const { t } = useTranslation();
  const profileForm = useUserProfileForm(user);
  const [saved, setSaved] = useState(false);
  const clearSaved = useCallback(() => setSaved(false), []);
  const hasUnsavedChanges = profileForm.hasUnsavedChanges;
  const canSubmit =
    hasUnsavedChanges && profileForm.hasValidInput && !profileForm.isSaving;

  useEffect(() => {
    onUnsavedChangesChange?.(hasUnsavedChanges);
  }, [hasUnsavedChanges, onUnsavedChangesChange]);

  useEffect(() => {
    onSavingChange?.(profileForm.isSaving);
  }, [onSavingChange, profileForm.isSaving]);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;
    setSaved(false);

    const profileSaved = await profileForm.saveProfile();
    if (!profileSaved) return;

    setSaved(true);
    onSaved?.();
  };

  return (
    <form onSubmit={onSubmit}>
      <ProfileSection title={t('プロフィール')}>
        {profileNotice}
        <UserProfileFields
          user={user}
          form={profileForm}
          disabled={profileForm.isSaving}
          onEdit={clearSaved}
        />
      </ProfileSection>

      {contractSection}

      <div className="space-y-3">
        {showSavedMessage && saved && !hasUnsavedChanges && (
          <Callout variant="success" size="compact">
            {t('設定を保存しました')}
          </Callout>
        )}
        {hasUnsavedChanges && (
          <Callout variant="warning" size="compact">
            {t('変更はまだ保存されていません')}
          </Callout>
        )}
      </div>

      <div
        data-testid="profile-card-footer"
        className="mt-6 border-t border-gray-200 pt-4"
      >
        <div className="flex justify-end">
          <Button
            type="submit"
            variant="primary"
            size="sm"
            isLoading={profileForm.isSaving}
            disabled={!canSubmit}
          >
            {t('保存')}
          </Button>
        </div>
      </div>
    </form>
  );
};
